import { createFileRoute } from '@tanstack/react-router';
import { formatDistanceToNowStrict } from 'date-fns';
import { CalendarClock, RefreshCw } from 'lucide-react';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

import {
    type DateTimeRange,
    DateTimeRangeFilter,
} from '@apps/web/components/admin/datetime_range_filter';
import { OrganizationCombobox } from '@apps/web/components/admin/organization_combobox';
import { AgentTab } from '@apps/web/components/admin/outreach_observability/agent_tab';
import {
    DEFAULT_OUTREACH_LIVE_WINDOW,
    MAX_OUTREACH_CUSTOM_RANGE_DAYS,
    MAX_OUTREACH_CUSTOM_RANGE_MS,
    OUTREACH_ANALYTICS_TIMEZONE,
    OUTREACH_LIVE_WINDOW_OPTIONS,
    OUTREACH_REFRESH_INTERVAL_MS,
    type OutreachCustomRange,
    type OutreachLiveWindow,
    type OutreachUrgencyFilter,
    outreachWindowSummary,
} from '@apps/web/components/admin/outreach_observability/constants';
import { InFlightRunsDialog } from '@apps/web/components/admin/outreach_observability/in_flight_board';
import { JudgeRunsDialog } from '@apps/web/components/admin/outreach_observability/judge_runs_dialog';
import { ModelAbTab } from '@apps/web/components/admin/outreach_observability/model_ab_tab';
import { OutcomesTab } from '@apps/web/components/admin/outreach_observability/outcomes_tab';
import { OutreachUrgencySelect } from '@apps/web/components/admin/outreach_observability/outreach_urgency_select';
import { PulseTab } from '@apps/web/components/admin/outreach_observability/pulse_tab';
import { QualityTab } from '@apps/web/components/admin/outreach_observability/quality_tab';
import { RunsDrillDialog } from '@apps/web/components/admin/outreach_observability/runs_drill_dialog';
import { StatusHero } from '@apps/web/components/admin/outreach_observability/status_hero';
import { TvTab } from '@apps/web/components/admin/outreach_observability/tv_tab';
import { Button } from '@apps/web/components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Spinner } from '@apps/web/components/ui/spinner';
import { Tabs, TabsList, TabsTrigger } from '@apps/web/components/ui/tabs';
import { cn } from '@apps/web/lib/utils';
import { useAdminOutreachObservability } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type StatusTile } from '@libraries/typescript/api_client';

const AGE_TICK_INTERVAL_MS = 10_000;

const STALE_AMBER_AGE_MS = 5 * 60_000;
const STALE_RED_AGE_MS = 15 * 60_000;

const OUTREACH_TAB_VALUES = [
    'pulse',
    'agent',
    'outcomes',
    'quality',
    'model_ab',
    'tv',
] as const;
type OutreachTab = (typeof OUTREACH_TAB_VALUES)[number];

type DisplayedPresetRange = {
    readonly selection: OutreachLiveWindow;
    readonly range: DateTimeRange;
};

const TAB_LABELS: Record<OutreachTab, string> = {
    pulse: 'Pulse',
    agent: 'Agent',
    outcomes: 'Outcomes',
    quality: 'Quality',
    model_ab: 'Model A/B',
    tv: 'TV',
};

function isOutreachTab(value: string): value is OutreachTab {
    return (OUTREACH_TAB_VALUES as ReadonlyArray<string>).includes(value);
}

const EMPTY_CUSTOM_RANGE: DateTimeRange = {
    after: undefined,
    before: undefined,
};

/**
 * Why a complete custom range cannot be applied, or null when it is valid.
 * An incomplete range (one bound or none) is also null — the preset window
 * simply stays active until both bounds are set.
 */
function customRangeError(range: DateTimeRange): string | null {
    if (!range.after || !range.before) {
        return null;
    }
    const startMs = Date.parse(range.after);
    const endMs = Date.parse(range.before);
    if (startMs >= endMs) {
        return 'Start must be before end.';
    }
    if (endMs - startMs > MAX_OUTREACH_CUSTOM_RANGE_MS) {
        return `Range is limited to ${MAX_OUTREACH_CUSTOM_RANGE_DAYS} days.`;
    }
    return null;
}

export const Route = createFileRoute('/_app/admin/outreach-observability')({
    component: OutreachObservabilityPage,
    validateSearch: z.object({
        tab: z.enum(OUTREACH_TAB_VALUES).catch('pulse'),
    }),
    head: () => ({
        meta: [{ title: 'Outreach Observability | Phoebe Admin' }],
    }),
});

function OutreachObservabilityPage() {
    const { tab } = Route.useSearch();
    const navigate = Route.useNavigate();

    const [orgIds, setOrgIds] = useState<Array<string>>([]);
    const [liveWindow, setLiveWindow] = useState<OutreachLiveWindow>(
        DEFAULT_OUTREACH_LIVE_WINDOW,
    );
    // A complete, valid custom range overrides the preset window; anything
    // else (empty, one bound, or invalid) leaves the preset active.
    const [customRange, setCustomRange] =
        useState<DateTimeRange>(EMPTY_CUSTOM_RANGE);
    const [displayedPresetRange, setDisplayedPresetRange] =
        useState<DisplayedPresetRange | null>(null);
    const [urgency, setUrgency] = useState<OutreachUrgencyFilter>('all');
    const rangeError = useMemo(
        () => customRangeError(customRange),
        [customRange],
    );
    const activeCustomRange: OutreachCustomRange | null =
        customRange.after && customRange.before && rangeError === null
            ? { start: customRange.after, end: customRange.before }
            : null;
    const windowSelection = activeCustomRange ?? liveWindow;

    // The route owns the always-visible chrome: the status hero and the
    // freshness indicator. Each tab fetches its own heavier sections.
    const {
        data: responseData,
        isValidating,
        mutate,
    } = useAdminOutreachObservability(
        {
            sections: ['live', 'status'],
            organizationIds: orgIds,
            window: windowSelection,
            urgency,
        },
        {
            refreshIntervalMs: OUTREACH_REFRESH_INTERVAL_MS,
        },
    );
    const data = responseData?.urgency === urgency ? responseData : undefined;

    useEffect(() => {
        if (
            customRange.after ||
            customRange.before ||
            data?.window !== liveWindow
        ) {
            return;
        }
        setDisplayedPresetRange((current) =>
            current?.selection === liveWindow
                ? current
                : {
                      selection: liveWindow,
                      range: {
                          after: data.pulse.window_start,
                          before: data.pulse.window_end,
                      },
                  },
        );
    }, [customRange.after, customRange.before, data, liveWindow]);

    const handleRefresh = useCallback(() => {
        void mutate();
    }, [mutate]);

    const handleOrgChange = useCallback((next: Array<string>) => {
        setOrgIds(next);
    }, []);

    // Choosing a preset always wins: it clears any custom range override.
    const handleWindowChange = useCallback((next: string) => {
        setCustomRange(EMPTY_CUSTOM_RANGE);
        setLiveWindow(next as OutreachLiveWindow);
    }, []);

    const handleUrgencyChange = useCallback((next: OutreachUrgencyFilter) => {
        setUrgency(next);
    }, []);

    const handleTabChange = useCallback(
        (next: string) => {
            if (!isOutreachTab(next)) {
                return;
            }
            void navigate({
                search: (prev) => ({ ...prev, tab: next }),
                replace: true,
            });
        },
        [navigate],
    );

    // A hero tile click opens an evidence dialog listing the contributing runs.
    // The route owns the open-state so the tiles stay presentational.
    const [openTile, setOpenTile] = useState<StatusTile | null>(null);
    const handleTileSelect = useCallback((tile: StatusTile) => {
        setOpenTile(tile);
    }, []);
    const handleTileDialogChange = useCallback((next: boolean) => {
        if (!next) {
            setOpenTile(null);
        }
    }, []);
    const goToTabFooter = useCallback(
        (tile: StatusTile) => (
            <Button
                variant="outline"
                size="sm"
                onClick={() => {
                    setOpenTile(null);
                    handleTabChange(tile.target_tab);
                }}
            >
                Go to{' '}
                {isOutreachTab(tile.target_tab)
                    ? TAB_LABELS[tile.target_tab]
                    : tile.target_tab}{' '}
                →
            </Button>
        ),
        [handleTabChange],
    );

    const [nowMs, setNowMs] = useState(() => Date.now());
    useEffect(() => {
        const intervalId = setInterval(() => {
            setNowMs(Date.now());
        }, AGE_TICK_INTERVAL_MS);
        return () => {
            clearInterval(intervalId);
        };
    }, []);

    // Capture the first echoed bounds for each preset instead of following
    // every polling response. The datetime inputs are intentionally
    // uncontrolled, so changing these values while someone is editing would
    // remount an input and discard its uncommitted segments.
    const presetRange =
        displayedPresetRange?.selection === liveWindow
            ? displayedPresetRange.range
            : EMPTY_CUSTOM_RANGE;
    // Editing one prefilled bound forms a complete custom range without
    // having to fill in the other.
    const displayedRange: DateTimeRange =
        customRange.after || customRange.before ? customRange : presetRange;

    const dataAsOf = data ? new Date(data.pulse.window_end) : null;
    const dataAgeMs = dataAsOf ? nowMs - dataAsOf.getTime() : null;
    // A custom range pins the payload's window_end to the selection, so its
    // age says nothing about data freshness — skip the staleness colors.
    const isCustomWindow = activeCustomRange !== null;

    const tabProps = {
        organizationIds: orgIds,
        window: windowSelection,
        urgency,
    };
    // The hero-tile numbers come from the live pulse fetched with the active
    // window selection,
    // so their drill-down dialogs must count over that same window.
    const windowSummary = outreachWindowSummary(windowSelection);

    return (
        <div className="flex h-full flex-1 flex-col overflow-hidden">
            <header className="bg-muted/40 border-b">
                <div className="flex items-center justify-between gap-4 px-6 py-4">
                    <div className="flex flex-col gap-1">
                        <h1 className="text-lg font-semibold tracking-tight">
                            Outreach Observability
                        </h1>
                        <p className="text-muted-foreground text-sm">
                            Live health of the callout outreach agents:
                            outcomes, outliers, intraday movement, and 14-day
                            regression trends.
                        </p>
                    </div>
                    <div className="text-muted-foreground flex flex-wrap items-center justify-end gap-3 text-xs">
                        <div className="w-64">
                            <OrganizationCombobox
                                value={orgIds}
                                onValueChange={handleOrgChange}
                                placeholder="All organizations"
                            />
                        </div>
                        <Select
                            value={liveWindow}
                            onValueChange={handleWindowChange}
                        >
                            <SelectTrigger
                                size="sm"
                                className={cn(
                                    'w-52',
                                    activeCustomRange !== null && 'opacity-50',
                                )}
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {OUTREACH_LIVE_WINDOW_OPTIONS.map((option) => (
                                    <SelectItem
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <DateTimeRangeFilter
                            label="Custom (ET)"
                            icon={CalendarClock}
                            showLabel={false}
                            range={displayedRange}
                            // The dashboard renders ET, so the inputs
                            // edit ET too.
                            timeZone={OUTREACH_ANALYTICS_TIMEZONE}
                            onChange={setCustomRange}
                        />
                        {rangeError !== null ? (
                            <span className="text-red-600 dark:text-red-400">
                                {rangeError}
                            </span>
                        ) : null}
                        <span
                            className={cn(
                                'tabular-nums',
                                !isCustomWindow &&
                                    dataAgeMs !== null &&
                                    dataAgeMs > STALE_AMBER_AGE_MS &&
                                    'font-semibold text-amber-600 dark:text-amber-400',
                                !isCustomWindow &&
                                    dataAgeMs !== null &&
                                    dataAgeMs > STALE_RED_AGE_MS &&
                                    'text-red-600 dark:text-red-400',
                            )}
                        >
                            {dataAsOf ? (
                                <Fragment>
                                    data as of{' '}
                                    {formatDistanceToNowStrict(dataAsOf, {
                                        addSuffix: true,
                                    })}
                                </Fragment>
                            ) : null}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRefresh}
                            disabled={isValidating}
                        >
                            {isValidating ? (
                                <Spinner size="sm" />
                            ) : (
                                <RefreshCw />
                            )}
                            Refresh
                        </Button>
                    </div>
                </div>
            </header>
            <div
                className={cn(
                    'flex min-h-0 flex-1 flex-col gap-4 p-6',
                    // The TV panel must stay viewport-bounded: no page scroll.
                    tab === 'tv' ? 'overflow-hidden' : 'overflow-y-auto',
                )}
            >
                {tab !== 'tv' ? (
                    <StatusHero
                        tiles={data ? (data.status_tiles ?? []) : undefined}
                        onTileSelect={handleTileSelect}
                    />
                ) : null}
                {openTile?.key === 'stuck_runs' ? (
                    <InFlightRunsDialog
                        open={true}
                        onOpenChange={handleTileDialogChange}
                        organizationIds={orgIds}
                        urgency={urgency}
                        footer={goToTabFooter(openTile)}
                    />
                ) : null}
                {openTile?.key === 'stale' ? (
                    <RunsDrillDialog
                        open={true}
                        onOpenChange={handleTileDialogChange}
                        title="Stale / abandoned runs"
                        description={`Stale or abandoned runs (${windowSummary}).`}
                        metric="stale"
                        organizationIds={orgIds}
                        urgency={urgency}
                        filters={{ window: windowSelection }}
                        footer={goToTabFooter(openTile)}
                    />
                ) : null}
                {openTile?.key === 'tool_errors' ? (
                    <RunsDrillDialog
                        open={true}
                        onOpenChange={handleTileDialogChange}
                        title="Tool-failure runs"
                        description={`Runs with at least one failed tool call (${windowSummary}).`}
                        metric="tool_failures"
                        organizationIds={orgIds}
                        urgency={urgency}
                        filters={{ window: windowSelection }}
                        footer={goToTabFooter(openTile)}
                    />
                ) : null}
                {openTile?.key === 'zero_suggestions' ? (
                    <RunsDrillDialog
                        open={true}
                        onOpenChange={handleTileDialogChange}
                        title="0-suggestion runs"
                        description={`Runs with zero suggested caregivers (${windowSummary}).`}
                        metric="zero_suggestions"
                        organizationIds={orgIds}
                        urgency={urgency}
                        filters={{ window: windowSelection }}
                        footer={goToTabFooter(openTile)}
                    />
                ) : null}
                {openTile?.key === 'cost_pace' ? (
                    <RunsDrillDialog
                        open={true}
                        onOpenChange={handleTileDialogChange}
                        title="Top spend runs"
                        description={`Ordered by cost, highest first (${windowSummary}).`}
                        metric="cost"
                        organizationIds={orgIds}
                        urgency={urgency}
                        filters={{ window: windowSelection }}
                        footer={goToTabFooter(openTile)}
                    />
                ) : null}
                {openTile?.key === 'judge_flags' ? (
                    <JudgeRunsDialog
                        open={true}
                        onOpenChange={handleTileDialogChange}
                        title="Judge-flagged runs"
                        flaggedOnly={true}
                        organizationIds={orgIds}
                        urgency={urgency}
                        footer={goToTabFooter(openTile)}
                    />
                ) : null}
                <div className="flex items-center justify-between gap-4">
                    <Tabs value={tab} onValueChange={handleTabChange}>
                        <TabsList>
                            {OUTREACH_TAB_VALUES.map((value) => (
                                <TabsTrigger key={value} value={value}>
                                    {TAB_LABELS[value]}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>
                    {tab !== 'tv' && tab !== 'model_ab' ? (
                        <OutreachUrgencySelect
                            value={urgency}
                            onValueChange={handleUrgencyChange}
                        />
                    ) : null}
                </div>
                {tab === 'pulse' ? <PulseTab {...tabProps} /> : null}
                {tab === 'agent' ? <AgentTab {...tabProps} /> : null}
                {tab === 'outcomes' ? <OutcomesTab {...tabProps} /> : null}
                {tab === 'quality' ? <QualityTab {...tabProps} /> : null}
                {tab === 'model_ab' ? (
                    <ModelAbTab
                        {...tabProps}
                        onUrgencyChange={handleUrgencyChange}
                    />
                ) : null}
                {tab === 'tv' ? <TvTab organizationIds={orgIds} /> : null}
            </div>
        </div>
    );
}

export default OutreachObservabilityPage;
